import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FileText, ArrowRight, Loader2, Building2 } from 'lucide-react';
import axios from 'axios';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DepartmentPage = () => {
  const { t } = useTranslation();
  const { departmentSlug } = useParams();
  const [department, setDepartment] = useState(null);
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDepartmentData = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/public/department/${departmentSlug}`);
        setDepartment(res.data.department);
        setForms(res.data.forms || []);
      } catch (err) {
        setError(err.response?.status === 404 ? 'not_found' : 'error');
      } finally {
        setLoading(false);
      }
    };
    fetchDepartmentData();
  }, [departmentSlug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error === 'not_found' || !department) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Building2 className="h-16 w-16 mx-auto text-slate-300 mb-4" />
          <h1 className="text-2xl font-bold text-slate-900">{t('departmentPage.departmentNotFound')}</h1>
          <p className="text-slate-600 mt-2">Die angeforderte Abteilung existiert nicht.</p>
          <Link to="/">
            <Button className="mt-4">{t('common.back')}</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-8 px-4" data-testid="department-page">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          {department.logo_url && (
            <img 
              src={`${API_URL}${department.logo_url}`} 
              alt={department.name}
              className="h-20 mx-auto mb-4 object-contain"
            />
          )}
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900">{department.name}</h1>
          {department.description && (
            <p className="text-slate-600 mt-2 max-w-2xl mx-auto">{department.description}</p>
          )}
        </div>

        {/* Forms Grid */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-slate-800 mb-4">{t('departmentPage.availableForms')}</h2>
          
          {forms.length === 0 ? (
            <Card className="border-slate-200/60">
              <CardContent className="py-12 text-center">
                <FileText className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-600">{t('departmentPage.noFormsAvailable')}</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {forms.map((form) => (
                <Card 
                  key={form.id} 
                  className="border-slate-200/60 hover:shadow-lg transition-all hover:border-blue-200 group"
                  data-testid={`form-card-${form.slug}`}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                        <FileText className="h-5 w-5 text-white" />
                      </div>
                      <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
                        Live
                      </Badge>
                    </div>
                    <CardTitle className="mt-3 text-lg">{form.title || form.name}</CardTitle>
                    {form.description && (
                      <CardDescription className="line-clamp-2">{form.description}</CardDescription>
                    )}
                  </CardHeader>
                  <CardContent>
                    <Link to={`/${departmentSlug}/${form.slug}`}>
                      <Button className="w-full group-hover:bg-blue-600">
                        {t('departmentPage.openForm')}
                        <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="text-center text-sm text-slate-500 mt-12">
          <p>UN-IT Forms</p>
        </div>
      </div>
    </div>
  );
};

export default DepartmentPage;
